import adsConfig from './adsConfig';

import {
    FB_CTA_OPTIONS,
    GOOGLE_DISPLAY_CTA_OPTIONS,
    FB_SINGLE,
    FB_CAROUSEL,
    FB_AND_INSTA_SINGLE,
    FB_AND_INSTA_CAROUSEL,
    INSTA_SINGLE,
    INSTA_CAROUSEL,
    GOOGLE_SEARCH,
    GOOGLE_DISPLAY,
    GOOGLE_DISPLAY_EXTERNAL,
    GOOGLE_DISPLAY_INTERNAL,
    GOOGLE_LEGACY_DISPLAY,
    GOOGLE_UNIVERSAL,
    GOOGLE_UNIVERSALV2,
    GOOGLE_VIDEO,
    GOOGLE_IMAGE,
    APPLE_SEARCH,
    KAKAO_IMAGE,
    KAKAO_IMAGE_CTA_OPTIONS,
    MICROSOFT_BING,
    YELP_SEARCH,
    OBJECTIVES,
    CHANNELS,
    YOUTUBE_VIDEO,
} from '@consts';

import {
    generateId,
    isValidAppStoreUrl,
    isValidPlayStoreUrl,
    isValidUrl,
    filterIdentity,
    result,
    truncStr,
    isWebOrForm,
} from '@util';

const DEFAULT_CONFIG = adsConfig.all.default;

const _defaultToObject = R.defaultTo({});

const isFbAndInstaSingle = R.compose(
    R.whereEq(FB_AND_INSTA_SINGLE),
    _defaultToObject
);

const isFbAndInstaCarousel = R.compose(
    R.whereEq(FB_AND_INSTA_CAROUSEL),
    _defaultToObject
);

const isFbSingle = R.compose(R.whereEq(FB_SINGLE), _defaultToObject);

const isFbCarousel = R.compose(R.whereEq(FB_CAROUSEL), _defaultToObject);

const isInstaSingle = R.compose(R.whereEq(INSTA_SINGLE), _defaultToObject);

const isInstaCarousel = R.compose(R.whereEq(INSTA_CAROUSEL), _defaultToObject);

const isGoogleDisplay = R.compose(R.whereEq(GOOGLE_DISPLAY), _defaultToObject);
const isGoogleDisplayExternal = R.compose(
    R.whereEq(GOOGLE_DISPLAY_EXTERNAL),
    _defaultToObject
);
const isGoogleDisplayInternal = R.compose(
    R.whereEq(GOOGLE_DISPLAY_INTERNAL),
    _defaultToObject
);
const isGoogleLegacyDisplay = R.compose(
    R.whereEq(GOOGLE_LEGACY_DISPLAY),
    _defaultToObject
);

const isGoogleSearch = R.compose(
    R.where({
        channel: R.compose(R.test(/google_search|google/gi), R.defaultTo('')),
        creativeType: R.compose(
            R.equals(GOOGLE_SEARCH.creativeType),
            R.defaultTo('')
        ),
    }),
    _defaultToObject
);

const isGoogleSearch_V1 = R.compose(
    R.allPass([isGoogleSearch, ({ creative } = {}) => !creative.titles]),
    _defaultToObject
);

const isGoogleUniversalApp = R.compose(
    R.whereEq(GOOGLE_UNIVERSAL),
    _defaultToObject
);

const isGoogleUniversalAppV2 = R.compose(
    R.whereEq(GOOGLE_UNIVERSALV2),
    _defaultToObject
);

const isGoogleVideo = R.compose(
    R.where({
        channel: R.compose(
            R.test(/google|google_video|youtube/gi),
            R.defaultTo('')
        ),
        creativeType: R.compose(R.test(/video/gi), R.defaultTo('')),
    }),
    _defaultToObject
);
const isGoogleImage = R.compose(R.whereEq(GOOGLE_IMAGE), _defaultToObject);
const isApple = R.compose(R.propEq('channel', 'apple'), _defaultToObject);
const isAppleSearch = R.compose(R.whereEq(APPLE_SEARCH), _defaultToObject);

const isFb = R.compose(R.propEq('channel', 'facebook'), _defaultToObject);
const isInsta = R.compose(R.propEq('channel', 'instagram'), _defaultToObject);
const isFbAndInsta = R.compose(
    R.propEq('channel', 'facebook+instagram'),
    _defaultToObject
);

const isFbSingleType = R.anyPass([
    isInstaSingle,
    isFbSingle,
    isFbAndInstaSingle,
]);
const isCarouselType = R.anyPass([
    isInstaCarousel,
    isFbCarousel,
    isFbAndInstaCarousel,
]);
const isFbFamily = R.anyPass([isFb, isInsta, isFbAndInsta]);
const isGoogleType = R.compose(R.propEq('channel', 'google'), _defaultToObject);

const isKakaoImage = R.compose(R.whereEq(KAKAO_IMAGE), _defaultToObject);
const isYelpSearch = R.compose(R.whereEq(YELP_SEARCH), _defaultToObject);
const isBing = R.compose(R.whereEq(MICROSOFT_BING), _defaultToObject);
const isKakao = R.compose(R.propEq('channel', 'kakao'), _defaultToObject);
const isYelp = R.compose(R.propEq('channel', 'yelp'), _defaultToObject);

const isYouTube = R.compose(R.propEq('channel', 'youtube'), _defaultToObject);

const isYouTubeVideo = R.compose(R.whereEq(YOUTUBE_VIDEO), _defaultToObject);

const isAmazon = R.compose(R.propEq('channel', 'amazon'), _defaultToObject);

const isNaverContentsAdInformation = R.compose(
    R.whereEq({
        channel: 'naver',
        creativeType: 'CONTENTS_AD_INFORMATION',
    }),
    _defaultToObject
);

const isMs = R.compose(R.propEq('channel', 'microsoft'), _defaultToObject);

const isGoogleDynamicSearch = R.compose(
    R.where({
        channel: R.compose(R.test(/google|google_search/gi), R.defaultTo('')),
        creativeType: R.compose(
            R.propEq('EXPANDED_DYNAMIC_SEARCH_AD'),
            R.defaultTo('')
        ),
    }),
    _defaultToObject
);

const isGoogleCallAd = R.compose(
    R.whereEq({
        channel: 'google',
        creativeType: 'CALL_AD',
    }),
    _defaultToObject
);

const isGoogleTextAd = R.compose(
    R.where({
        channel: R.equals('google'),
        creativeType: R.test(/text_ad/gi),
    }),
    _defaultToObject
);

function fillAd(ad = {}) {
    ad = R.over(R.lensProp('creativeId'), R.defaultTo(generateId()), ad);

    const _creativeOver = R.over(R.lensPath(['creative', 'callToAction']));
    const defaultTo = R.curry(
        (defaultValue, value) => value || R.clone(defaultValue)
    );

    ad = R.cond([
        [isFbSingleType, _creativeOver(defaultTo('LEARN_MORE'))],
        [
            isCarouselType,
            R.over(
                R.lensPath(['creative', 'cards']),
                R.compose(
                    R.map(
                        R.over(
                            R.lensProp('callToAction'),
                            defaultTo('NO_BUTTON')
                        )
                    ),
                    R.defaultTo([])
                )
            ),
        ],
        [isGoogleDisplay, _creativeOver(defaultTo('Learn More'))],
        [isKakaoImage, _creativeOver(defaultTo('알아보기'))],
        [R.T, R.identity],
    ])(ad);

    ad = R.over(R.lensProp('meta'), defaultTo({ isEditing: true }), ad);

    return ad;
}

function getAdConfig(ad) {
    let { channel, creativeType } = ad || {};
    // if (channel === CHANNELS.FB_AND_INSTA) {
    //     channel = 'facebook';
    // }

    return R.pathOr(DEFAULT_CONFIG, [channel, creativeType], adsConfig);
}

const getDefaultAd = (ad) =>
    R.compose(
        R.defaultTo({}),
        result('getDefault'),
        R.defaultTo({}),
        getAdConfig,
        R.defaultTo({})
    )(ad);

const _findMedia = R.compose(R.find(R.propEq('key', 'media')), R.defaultTo([]));

function getMediaValidations(ad, creative) {
    var param = _findMedia(getAdConfig(ad).params);
    if (!param) {
        return;
    }
    return R.defaultTo(
        [],
        R.defaultTo(R.always([]), param.validate).call(param, creative)
    );
}

function executable(ad = {}, options = {}) {
    return R.compose(
        (func) => func(ad.creative, options),
        R.propOr(R.always({ value: true }), 'executable'),
        R.defaultTo({}, getAdConfig)
    )(ad);
}

const ctaOptions = R.compose(
    R.defaultTo([]),
    R.cond([
        [
            isFbSingleType,
            () => R.reject(R.propEq('value', 'NO_BUTTON'), FB_CTA_OPTIONS),
        ],
        [isCarouselType, R.always(FB_CTA_OPTIONS)],
        [isGoogleType, R.always(GOOGLE_DISPLAY_CTA_OPTIONS)],
        [isKakaoImage, R.always(KAKAO_IMAGE_CTA_OPTIONS)],
        [R.T, R.always([])],
    ]),
    _defaultToObject
);

function updateFrom(from, to) {
    if (from === to) {
        return to;
    }
    from = R.clone(from);
    to = R.clone(to);

    return R.compose(
        R.defaultTo(to),
        (func) => (func ? func(from, to) : to),
        R.prop('updateFrom'),
        R.defaultTo({}, getAdConfig)
    )(to);
}

const ctaMapper = (trans = R.identity, ad) => {
    if (isFbFamily(ad)) {
        return (value) => R.assoc('text', trans(value.text), value);
    } else if (isGoogleDisplay(ad)) {
        return R.compose(
            R.applySpec({
                text: R.identity,
                value: R.identity,
            }),
            trans,
            R.concat('COMMON.cta_google_')
        );
    } else if (isKakaoImage(ad)) {
        return R.applySpec({
            text: R.identity,
            value: R.identity,
        });
    } else {
        return R.identity;
    }
};

const _appProps = (creative = {}) =>
    [creative.appStoreUrl, creative.playStoreUrl].map(
        R.compose(R.trim(), R.defaultTo(''))
    );

const extractAppUrls = R.compose(
    R.filter(R.identity),
    R.flatten,
    R.map(
        R.cond([
            [
                isCarouselType,
                R.pipe(
                    R.path(['creative', 'cards']),
                    R.map(_appProps),
                    R.flatten
                ),
            ],
            [R.T, R.pipe(R.prop('creative'), _appProps)],
        ])
    ),
    R.defaultTo([])
);

const appUrlExist = R.compose(
    R.both(R.complement(R.isNil), R.complement(R.isEmpty)),
    extractAppUrls
);

function getAdModalText({ ad = {}, reason = {} }) {
    const paramName = reason.paramName;
    switch (reason.type) {
        case 'channel':
            return;
        case 'tooLong':
            return this.$t('PROPOSAL.ads_should_be_less', {
                paramName: this.$t(
                    'PROPOSAL.ad_config_google_display_description'
                ),
                max: reason.maxLength,
            });
        case 'missing':
            if (isKakaoImage(ad)) {
                switch (paramName) {
                    case 'profileName':
                        return this.$t('PROPOSAL.kakao_profile_name_missing');
                    case 'logo':
                        return this.$t('PROPOSAL.kakao_logo_missing');
                    case 'description':
                        return this.$t('PROPOSAL.kakao_description_missing');
                    case 'title':
                        return this.$t('PROPOSAL.kakao_title_missing');
                }
            }

            if (paramName === 'media') {
                return this.$t('PROPOSAL.ads_media_missing');
            } else if (paramName === 'image') {
                return this.$t('PROPOSAL.ads_image_missing');
            } else if (
                R.test(/description|mainDescription|text1/gi, paramName)
            ) {
                return this.$t('PROPOSAL.ads_description_missing');
            } else if (
                R.test(/title1|title2|longTitle|shortTitle/gi, paramName)
            ) {
                return this.$t('PROPOSAL.ads_title_missing');
            } else if (paramName === 'businessName') {
                return this.$t('PROPOSAL.ads_business_name_missing');
            } else if (paramName === 'facebook_page') {
                return;
            } else if (paramName === 'displayUrl') {
                return this.$t('PROPOSAL.ads_display_url_missing');
            } else if (paramName === 'url') {
                return this.$t('PROPOSAL.ads_url_missing');
            } else if (paramName === 'titles') {
                return this.$t('PROPOSAL.ads_title_missing');
            } else if (paramName === 'callToAction') {
                return this.$t('PROPOSAL.ads_call_to_action_missing');
            }

            return `${reason.paramName} missing`;
        case 'invalid':
            if (reason.paramName === 'url') {
                return this.$t('PROPOSAL.ads_invalid_url');
            } else if (reason.paramName === 'appUrl') {
                return this.$t('PROPOSAL.ads_invalid_url');
            } else if (reason.paramName === 'media') {
                if (reason.ratio || reason.minWidth || reason.minHeight) {
                    return this.$t(
                        `PROPOSAL.ads_invalid_video_dimension_size_${ad.channel}`
                    );
                } else if (reason.tooLong) {
                    return this.$t('PROPOSAL.ads_invalid_video_length', {
                        value: reason.tooLong,
                    });
                }
            }
            break;
        case 'notEnoughCards':
            return 'Upload at least 3 images or videos to run ads in a slide';
        default:
            return reason.type;
    }
}

const ad = R.curryN(2, (_ = '', { channel = '', creativeType = '' }) => {
    if (!channel || !creativeType) {
        return;
    }

    const config = getAdConfig({ channel, creativeType }) || {};
    let { adType, subChannel = [] } = config;
    return fillAd({
        ...getDefaultAd({ channel, creativeType }),
        enabled: true,
        executable: { value: false },
        adType,
        subChannel,
    });
});

function ads(businessName = '', data = []) {
    return data.map(ad(businessName)).filter(R.identity);
}

function adjustDuplicate(ads = []) {
    const idEq = R.propEq('creativeId');
    const findIndex = R.findIndex(R.__, ads);

    return ads
        .map((ad, idx) => {
            const finded = findIndex(idEq(ad.creativeId));

            if (finded !== idx || !ad.creativeId) {
                ad.creativeId = generateId();
            }
            return ad;
        })
        .filter(R.identity);
}

function validateWebOrApp({
    type = '',
    websiteUrl,
    appStoreUrl,
    playStoreUrl,
} = {}) {
    if (!type) {
        return false;
    }
    const isWeb = R.test(/web/gi);
    const isApp = R.test(/app/gi);

    if (isWeb(type)) {
        if (!isValidUrl(websiteUrl)) {
            return false;
        }
    } else if (isApp(type)) {
        if (!appStoreUrl && !playStoreUrl) {
            return false;
        }

        if (appStoreUrl && !isValidAppStoreUrl(appStoreUrl)) {
            return false;
        }

        if (playStoreUrl && !isValidPlayStoreUrl(playStoreUrl)) {
            return false;
        }
    }

    return true;
}

const converCreativeType = (emitted = [], generated = []) => {
    const _find = R.find(R.__, generated);

    //prioirty
    // 1. exact
    // 2. channel
    // 3. as it is
    return emitted.map(
        (data) =>
            _find(R.equals(data)) ||
            _find(R.propEq('channel', data.channel)) ||
            data
    );
};

const kakaoAddable = ({ user = {} } = {}) => {
    return user.country === 'KR';
};

const adsSelection = ({
    type = 'web',
    appstoreAvailable = true,
    channel = '',
    user = {},
    objectives: [objective] = [],
    hasCombinedFBAndInstagram,
}) => {
    let options = [];
    let selected = [];

    const _kakaoImage = kakaoAddable({
        user,
    })
        ? KAKAO_IMAGE
        : null;

    if (isWebOrForm(type)) {
        options = [
            FB_SINGLE,
            INSTA_SINGLE,
            GOOGLE_SEARCH,
            GOOGLE_DISPLAY,
            _kakaoImage,
            // YOUTUBE_VIDEO,
            // MICROSOFT_BING,
        ];

        selected = R.take(3, options);
    } else if (R.test(/app/gi, type)) {
        const _appstore = appstoreAvailable ? APPLE_SEARCH : null;
        options = [FB_SINGLE, INSTA_SINGLE, GOOGLE_UNIVERSALV2, _appstore];
        selected = R.take(4, options);
    }

    const channelFilter = channel
        ? R.filter(R.propEq('channel', channel))
        : R.identity;

    const innerJoin = R.innerJoin(
        ({ channel }, _channel) => channel === _channel,
        R.__,
        availableChannels({ objective, type })
    );

    options = R.compose(innerJoin, filterIdentity)(options);

    selected = R.compose(innerJoin, channelFilter, filterIdentity)(selected);

    if (hasCombinedFBAndInstagram) {
        options.unshift(FB_AND_INSTA_SINGLE);
    }

    return {
        options,
        selected,
    };
};

const convertToString = R.cond([
    [isInsta, R.always('instagramFeed')],
    [isFb, R.always('facebookFeed')],
    [isGoogleDisplay, R.always('googleDisplay')],
    [isGoogleSearch, R.always('googleSearch')],
    [isGoogleUniversalApp, R.always('googleUniversalApp')],
    [isAppleSearch, R.always('appleSearch')],
    [isKakaoImage, R.always('kakaoImage')],
]);

const filterNewAds = (exists = [], ads = []) => {
    return R.differenceWith(
        R.eqBy(R.pick(['channel', 'creativeType'])),
        ads,
        exists
    );
};

const paramsObject = R.compose(
    R.reduce((acc, param = {}) => R.assoc(param.key, param, acc), {}),
    R.propOr([], 'params'),
    getAdConfig
);

const cutAdCreative = (ad = {}) => {
    if (!ad.channel || !ad.creativeType) {
        return ad;
    }

    const params = R.propOr({}, R.__, paramsObject(ad));

    return {
        ...ad,
        creative: R.mapObjIndexed((value, key) => {
            const { maxLength } = params(key);
            return !value || !maxLength || typeof value !== 'string'
                ? value
                : truncStr(value, maxLength, getAdConfig(ad).counter);
        }, ad.creative),
    };
};

function availableChannels({ objective, type = 'web', excludes = [] }) {
    const {
        GOOGLE,
        FACEBOOK,
        INSTAGRAM,
        MICROSOFT,
        KAKAO,
        APPLE,
        YELP,
        YOUTUBE,
        GOOGLE_DISPLAY,
        GOOGLE_SEARCH,
        GOOGLE_UNI_APP,
    } = CHANNELS;

    const CHANNEL_LIST = [
        GOOGLE,
        FACEBOOK,
        INSTAGRAM,
        APPLE,
        KAKAO,
        MICROSOFT,
        YELP,
        YOUTUBE,
        GOOGLE_DISPLAY,
        GOOGLE_SEARCH,
        GOOGLE_UNI_APP,
    ];

    if (!objective) {
        return CHANNEL_LIST;
    }

    excludes = [YOUTUBE];

    switch (objective) {
        case OBJECTIVES.CONVERSIONS:
            if (isWebOrForm(type)) {
                excludes = [
                    ...excludes,
                    MICROSOFT,
                    KAKAO,
                    APPLE,
                    GOOGLE,
                    GOOGLE_UNI_APP,
                ];
            } else {
                excludes = [...excludes, MICROSOFT, KAKAO];
            }
            break;
        case OBJECTIVES.REACH:
            excludes = [
                ...excludes,
                GOOGLE,
                MICROSOFT,
                KAKAO,
                APPLE,
                GOOGLE_UNI_APP,
            ];
            excludes = R.reject(R.equals(YOUTUBE), excludes);
            break;
        case OBJECTIVES.LINK_CLICKS:
            excludes = [...excludes, APPLE, GOOGLE_UNI_APP];
            break;
        case OBJECTIVES.LEAD_GENERATION:
            excludes = [...excludes, APPLE, KAKAO, GOOGLE_UNI_APP];
            break;
        default:
            break;
    }

    // TODO REMOVE after MS comeback
    excludes = [...excludes, MICROSOFT, YELP];

    return R.without(excludes, CHANNEL_LIST);
}

function baseType({ objective = '', webOrApp = {} }) {
    const { LEAD_GENERATION, CONVERSIONS, APP_INSTALLS } = OBJECTIVES;
    const value = R.propOr('app', 'type', webOrApp);

    if (!objective) {
        return value;
    } else {
        switch (objective) {
            case LEAD_GENERATION:
                return 'form';
            case CONVERSIONS:
                return value;
            case APP_INSTALLS:
                return 'app';
            default:
                return 'web';
        }
    }
}

export const GET_FB_INSTA_SKELETON = () => {
    return {
        meta: { adEdited: false, isEditing: false },
        adType: 'link',
        channel: 'facebook',
        enabled: false,
        groupId: 1,
        creative: {},
        creativeId: generateId(),
        executable: { value: false },
        subChannels: ['facebook', 'instagram'],
        creativeType: 'photo',
    };
};

export {
    getAdConfig,
    getMediaValidations,
    executable,
    updateFrom,
    ctaOptions,
    fillAd,
    isFbAndInstaSingle,
    isFbAndInstaCarousel,
    isFbSingle,
    isInstaSingle,
    isFbCarousel,
    isInstaCarousel,
    isGoogleDisplay,
    isGoogleDisplayExternal,
    isGoogleDisplayInternal,
    isGoogleLegacyDisplay,
    isGoogleSearch,
    isGoogleUniversalApp,
    isGoogleUniversalAppV2,
    isGoogleVideo,
    isGoogleImage,
    isApple,
    isAppleSearch,
    isFbSingleType,
    isCarouselType,
    isFbFamily,
    isGoogleType,
    isFb,
    isInsta,
    isFbAndInsta,
    isYouTube,
    isYouTubeVideo,
    ctaMapper,
    appUrlExist,
    getAdModalText,
    ad,
    ads,
    adjustDuplicate,
    validateWebOrApp,
    converCreativeType,
    adsSelection,
    isKakaoImage,
    convertToString,
    extractAppUrls,
    filterNewAds,
    getDefaultAd,
    isBing,
    isYelpSearch,
    isKakao,
    isAmazon,
    isYelp,
    paramsObject,
    cutAdCreative,
    availableChannels,
    baseType,
    isNaverContentsAdInformation,
    isMs,
    isGoogleDynamicSearch,
    isGoogleCallAd,
    isGoogleTextAd,
    isGoogleSearch_V1,
};
